import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
    Button,
    Loading,
    MaintenanceDurationPicker,
    MM_DEFAULT_MINUTES,
    Modal,
    Tabs
} from '../root/components/common'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import * as actions from './actions'
import { reportsRequest } from 'web/src/app/reports/actions'
import { DEVICE_TYPE, OPERATION_MODES, TRANSFER_MODE } from '../root/constants'
import { getHasTomDevices } from '../root/actions'
import { Box } from '@material-ui/core'
import {
    getBatteryPercentage,
    getFormattedDate,
    getReportTypeDescriptionText,
    isJerry3 as isJerry3Helper
} from '../../utils'
import cl from 'classnames'
import { UserPermissions } from '../../services/permissions'
import { DevicePictures } from './components/device-pictures'
import { isTomDevice } from '../root/helpers/is-tom-device'
import { DeviceActions } from './components/device-actions'
import { DeviceReports } from './components/device-reports'
import { ReportCatchModal } from './components/report-catch-modal'
import * as timeago from 'timeago.js'
import de from 'timeago.js/lib/lang/de'
import nl from 'timeago.js/lib/lang/nl'
import pt from 'timeago.js/lib/lang/pt_BR'
import es from 'timeago.js/lib/lang/es'
import fr from 'timeago.js/lib/lang/fr'
import cs from 'timeago.js/lib/lang/cs'
import moment from 'moment'
import { getInitialFiltersState } from '../root/hooks/useReportsFilters'
import DeviceOverview from './components/device-overview'
import { useReportsFilters } from 'web/src/app/root/hooks/useReportsFilters.js'
import { urlToSearchState } from 'web/src/utils/hooks/useSyncUrlState.js'
import { useStyles } from './styles'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import TimeAgo from 'timeago-react'
import { useReportTypes } from '../root/hooks/useReportTypes'
import { DeviceMoveHistory } from './components/device-move-history'
import { DeviceSettings } from './components/device-settings'
import { useQuery } from 'react-query'
import { getLocationSettings } from '../locations/api/location-settings-api'
import { getRouterBySerial } from '../network-devices/api/network-devices-api'

timeago.register('nl', nl)
timeago.register('de', de)
timeago.register('pt', pt)
timeago.register('es', es)
timeago.register('fr', fr)
timeago.register('cs', cs)

function Device(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const deviceId = props.match.params.deviceId
    const tab = props.match.params.tab || 'overview'
    const { isLoading, locale } = useSelector(state => state.main)
    const { device, deviceImages, deviceImagesLoader, deviceReports } =
        useSelector(state => state.devices)

    const [isConfirmModal, setIsConfirmModal] = useState(false)

    const [isResetModalOpen, setIsResetModalOpen] = useState(false)
    const [isReportCatchModalOpen, setIsReportCatchModalOpen] = useState(false)
    const [isSetOfflineModalOpen, setIsSetOfflineModalOpen] = useState(false)
    const [isBatteryModalOpen, setIsBatteryModalOpen] = useState(false)
    const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false)
    const [maintenanceMinutes, setMaintenanceMinutes] =
        useState(MM_DEFAULT_MINUTES)
    const [isDurationValid, setIsDurationValid] = useState(true)

    const [isSetActiveTOMModalOpen, setIsSetActiveTOMModalOpen] =
        useState(false)

    const permissions = {
        updateDevice:
            (UserPermissions.device(device && device.customer_id).update &&
                device?.is_assigned) ||
            (device?.is_original && UserPermissions.isAllOriginalGranted())
    }

    const isTom = useMemo(
        () => isTomDevice(device?.device_type),
        [device?.device_type]
    )
    const isJerryLora = useMemo(
        () => device?.device_type === DEVICE_TYPE['jerry-lora'],
        [device?.device_type]
    )
    const isJerryBaitRat = useMemo(
        () => device?.device_type === DEVICE_TYPE['jerry-bait-rat'],
        [device?.device_type]
    )
    const isJerry3 = useMemo(() => {
        if (isJerryLora || isJerryBaitRat) {
            return isJerry3Helper(device?.version, device?.pcb_revision)
        }
        return false
    }, [isJerryLora, isJerryBaitRat, device?.version, device?.pcb_revision])
    const isTrapMe = useMemo(() => {
        return device?.device_type === DEVICE_TYPE['tm01-1']
    }, [device?.device_type])
    const hasOccupancyRate = useMemo(() => {
        return isTom && device?.pest_control_system_id === 10
    }, [isTom, device?.pest_control_system_id])
    const isInsectTypeTom = useMemo(() => {
        return isTom && device?.operation_mode === OPERATION_MODES.insect
    }, [isTom, device?.operation_mode])
    const isDeviceTriggered = useMemo(() => device?.last_report_type == '2')
    const { severity } = useReportTypes(device?.last_report_type)
    const isTrapSensor = useMemo(
        () => device?.device_type === DEVICE_TYPE.trapsensor,
        [device?.device_type]
    )

    const [imagesForUpload, setImagesForUpload] = useState([])
    const [deletedPictureKeys, setDeletedPictureKeys] = useState([])
    const [deletingImagesSuccess, setDeletingImagesSuccess] = useState(false)
    const { filters } = useReportsFilters(
        urlToSearchState(location),
        UserPermissions.isAllOriginalGranted(),
        deviceId,
        null
    )
    const { reports } = useSelector(state => state.reports)

    const { data: locationSettings } = useQuery(
        ['location-settings', device?.location_id],
        () => getLocationSettings(device?.location_id),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: !!device?.location_id,
            onError: () => {
                console.error('Error fetching location settings')
            }
        }
    )

    const loraGatewayId = useMemo(
        () =>
            device?.transfer_mode === TRANSFER_MODE.lora
                ? device?.data?.LoRa?.gw_id
                : undefined,
        [device?.transfer_mode, device?.data?.LoRa?.gw_id]
    )

    const {
        data: gateway,
        isLoading: isLoadingGateway,
        isError: isGatewayError
    } = useQuery(
        ['router-by-serial', loraGatewayId],
        () => getRouterBySerial(loraGatewayId),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: !!loraGatewayId
        }
    )

    useEffect(() => {
        if (deviceImages[deviceId]) {
            setImagesForUpload(deviceImages[deviceId])
        }
    }, [deviceImages[deviceId]])

    const onReset = deviceId => {
        dispatch(
            actions.resetDeviceRequest(
                deviceId,
                getInitialFiltersState(UserPermissions.isAllOriginalGranted())
            )
        )
        setIsResetModalOpen(false)
    }

    const onReportCatchClick = () => {
        setIsReportCatchModalOpen(true)
    }

    const closeReportCatchModal = () => setIsReportCatchModalOpen(false)

    const onSetOffline = deviceId => {
        dispatch(
            actions.setOfflineRequest(
                deviceId,
                getInitialFiltersState(UserPermissions.isAllOriginalGranted())
            )
        )
        setIsSetOfflineModalOpen(false)
    }

    const onBatteryReset = deviceId => {
        dispatch(actions.batteryResetRequest(deviceId))
        setIsBatteryModalOpen(false)
    }

    const onSetActiveTOM = deviceId => {
        dispatch(actions.setStatusActiveTOMRequest(deviceId))
        setIsSetActiveTOMModalOpen(false)
    }

    const onMaintenanceModeSet = (deviceId, inMaintenanceMode) => {
        dispatch(
            actions.setMaintenanceMode(
                deviceId,
                inMaintenanceMode,
                maintenanceMinutes
            )
        )
        setIsMaintenanceModalOpen(false)
    }

    const onSetCurrentTab = tabKey => {
        props.history.push(`/devices/${deviceId}/${tabKey}`)
    }

    const onConfirmThresholdReset = useCallback(() => {
        dispatch(actions.deviceRequest(deviceId))
    }, [deviceId])

    const onConfirmReplaceBait = useCallback(() => {
        dispatch(actions.deviceRequest(deviceId))
    }, [deviceId])

    useEffect(() => {
        dispatch(actions.deviceRequest(deviceId))
        if (tab !== 'reports') {
            dispatch(
                actions.deviceReportsRequest(deviceId, {
                    from: moment().subtract(28, 'days').unix()
                })
            )
        }
        dispatch(getHasTomDevices())
        /* return () => {
            dispatch(actions.deviceRequestSuccess(null))
        } */
    }, [])

    const JERRY_TABS = {
        overview: {
            key: 'overview',
            name: locale.locations.overview
        },
        reports: {
            key: 'reports',
            name: locale.locations.reports
        },
        history: {
            key: 'device_move_history',
            name: locale.devices.history
        }
    }
    const JERRY_BAIT_RAT_TABS = {
        ...JERRY_TABS,
        settings: {
            key: 'settings',
            name: locale.common.settings
        }
    }
    const TOM_TABS = {
        ...JERRY_TABS,
        pictures: {
            key: 'pictures',
            name: locale.locations.pictures
        },
        history: {
            key: 'device_move_history',
            name: locale.devices.history
        }
    }
    const handleDeviceAction = action => {
        if (action === 'set_offline') {
            return setIsSetOfflineModalOpen(true)
        }
        if (action === 'report_catch') {
            return onReportCatchClick()
        }
        if (action === 'reset_device') {
            return setIsResetModalOpen(true)
        }
        if (action === 'reset_tom_status') {
            return setIsSetActiveTOMModalOpen(true)
        }
        if (action === 'reset_battery') {
            return setIsBatteryModalOpen(true)
        }
        if (action === 'set_maintenance_mode') {
            return setIsMaintenanceModalOpen(true)
        }
    }

    const submitUploadImages = () => {
        if (deletedPictureKeys.length) {
            dispatch(
                actions.deleteDeviceImages({
                    deviceId,
                    customerId: device.customer_id,
                    deletedPictureKeys,
                    setDeletedPictureKeys,
                    setDeletingImagesSuccess
                })
            )
        } else {
            dispatch(
                actions.postDeviceImages({
                    deviceId,
                    customerId: device.customer_id,
                    images: imagesForUpload.filter(image => {
                        return !image.Body
                    }),
                    deletedPictureKeys
                })
            )
        }
    }

    useEffect(() => {
        if (imagesForUpload.length) {
            if (deletingImagesSuccess) {
                dispatch(
                    actions.postDeviceImages({
                        deviceId,
                        customerId: device.customer_id,
                        images: imagesForUpload.filter(image => {
                            return !image.Body
                        })
                    })
                )

                setDeletingImagesSuccess(false)
            }
        }
    }, [deletingImagesSuccess])

    const getImagesByDeviceId = () => {
        dispatch(actions.getImagesByDeviceId(deviceId))
    }

    useEffect(() => {
        getImagesByDeviceId()
    }, [deviceId])

    useEffect(() => {
        filters.take = 3
        filters.deviceId = deviceId

        dispatch(reportsRequest(filters))
    }, [filters])

    const getLastTriggeredDate = () => {
        const triggeredReport = deviceReports?.find(dr => dr.report_type == '2')
        return triggeredReport?.updated_at
    }

    const lastTriggeredDateText = () => {
        const lastTriggeredDate = getLastTriggeredDate()
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        if (lastTriggeredDate) {
            return (
                <>
                    {getFormattedDate(lastTriggeredDate)}
                    <span className={classes.timeAgo}>
                        (
                        <TimeAgo
                            datetime={lastTriggeredDate}
                            locale={locale_key}
                        />
                        )
                    </span>
                </>
            )
        } else return '/'
    }

    return (
        <Box>
            <Tabs
                className={classes.tabs}
                tabs={
                    isTom
                        ? TOM_TABS
                        : isJerryBaitRat
                        ? JERRY_BAIT_RAT_TABS
                        : JERRY_TABS
                }
                currentTab={tab}
                setCurrentTab={onSetCurrentTab}
                centered={false}
            />
            <Box className={classes.wrapper}>
                {device && (
                    <React.Fragment>
                        {tab === 'overview' && (
                            <>
                                {isLoading ? (
                                    <Loading />
                                ) : (
                                    <>
                                        <DeviceOverview>
                                            <DeviceOverview.DeviceSectionGroup>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        width: '80%'
                                                    }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            maxWidth: '50%',
                                                            marginRight: 40
                                                        }}>
                                                        <DeviceOverview.DeviceImages
                                                            imagesForUpload={
                                                                imagesForUpload
                                                            }
                                                            setImagesForUpload={
                                                                setImagesForUpload
                                                            }
                                                            setDeletedPictureKeys={
                                                                setDeletedPictureKeys
                                                            }
                                                            deletedPictureKeys={
                                                                deletedPictureKeys
                                                            }
                                                            kind="avatar"
                                                            submitUploadImages={
                                                                submitUploadImages
                                                            }
                                                            loading={
                                                                deviceImagesLoader
                                                            }
                                                            device={device}
                                                            permissions={
                                                                permissions
                                                            }
                                                            getImagesByDeviceId={
                                                                getImagesByDeviceId
                                                            }
                                                        />
                                                        <DeviceOverview.DeviceNameAndBattery
                                                            batteryPercentage={`${getBatteryPercentage(
                                                                device.battery_status
                                                            )}% `}
                                                            batteryStatusValue={
                                                                device.battery_status
                                                            }
                                                            device={device}
                                                            setIsConfirmModal={
                                                                setIsConfirmModal
                                                            }
                                                            setIsBatteryModalOpen={
                                                                setIsBatteryModalOpen
                                                            }
                                                            permissions={
                                                                permissions
                                                            }
                                                            isTom={isTom}
                                                            isJerryLora={
                                                                isJerryLora
                                                            }
                                                            isJerryBaitRat={
                                                                isJerryBaitRat
                                                            }
                                                            handleDeviceAction={
                                                                handleDeviceAction
                                                            }
                                                        />
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            maxWidth: '50%'
                                                        }}>
                                                        <DeviceOverview.DeviceBasicInfo
                                                            deviceStatusText={getReportTypeDescriptionText(
                                                                {
                                                                    report_type:
                                                                        device.last_report_type,
                                                                    data: device
                                                                },
                                                                locale
                                                            )}
                                                            device={device}
                                                            isTom={isTom}
                                                            severity={severity}
                                                            isJerryBaitRat={
                                                                isJerryBaitRat
                                                            }
                                                            locationSettings={
                                                                locationSettings
                                                            }
                                                        />
                                                    </div>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'end',
                                                        width: '20%'
                                                    }}>
                                                    {permissions.updateDevice && (
                                                        <DeviceActions
                                                            isJerry3={isJerry3}
                                                            isTom={isTom}
                                                            isJerryLora={
                                                                isJerryLora
                                                            }
                                                            isJerryBaitRat={
                                                                isJerryBaitRat
                                                            }
                                                            isTrapSensor={
                                                                isTrapSensor
                                                            }
                                                            isTrapMe={isTrapMe}
                                                            label={
                                                                locale.devices
                                                                    .deviceActions
                                                            }
                                                            device={device}
                                                            onBtnPress={
                                                                handleDeviceAction
                                                            }
                                                            buttonStyles={{
                                                                width: 190
                                                            }}
                                                            className={
                                                                classes.deviceActionButton
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </DeviceOverview.DeviceSectionGroup>
                                            {loraGatewayId ? (
                                                <DeviceOverview.DeviceSectionGroup>
                                                    <DeviceOverview.DeviceGatewayCard
                                                        gateway={gateway}
                                                        isLoading={
                                                            isLoadingGateway
                                                        }
                                                        isError={isGatewayError}
                                                        gwId={loraGatewayId}
                                                        deviceId={deviceId}
                                                    />
                                                </DeviceOverview.DeviceSectionGroup>
                                            ) : null}
                                            <DeviceOverview.DeviceSectionGroup>
                                                <Box
                                                    className={
                                                        classes.reportsAndHistory
                                                    }>
                                                    <DeviceOverview.DeviceReports
                                                        reports={reports}
                                                        device={device}
                                                    />
                                                    <DeviceOverview.DeviceMoveHistory
                                                        device={device}
                                                    />
                                                </Box>
                                            </DeviceOverview.DeviceSectionGroup>
                                            {isJerryBaitRat &&
                                                !device.in_maintenance_mode && (
                                                    <DeviceOverview.BaitDebounceNotice
                                                        onOpenMaintenanceMode={
                                                            permissions.updateDevice
                                                                ? () =>
                                                                      handleDeviceAction(
                                                                          'set_maintenance_mode'
                                                                      )
                                                                : null
                                                        }
                                                    />
                                                )}
                                            {isJerryBaitRat && (
                                                <DeviceOverview.DeviceSectionGroup>
                                                    <Box
                                                        className={
                                                            classes.baitActivitySection
                                                        }>
                                                        <DeviceOverview.DeviceBaitCard
                                                            device={device}
                                                            onSetCurrentTab={
                                                                onSetCurrentTab
                                                            }
                                                            overridingLocationSettings={
                                                                locationSettings
                                                            }
                                                            canUpdate={
                                                                permissions.updateDevice
                                                            }
                                                            onConfirmReplaceBait={
                                                                onConfirmReplaceBait
                                                            }
                                                        />
                                                        <DeviceOverview.DeviceBaitActivitiesCard
                                                            device={device}
                                                            onConfirmThresholdReset={
                                                                onConfirmThresholdReset
                                                            }
                                                            onSetCurrentTab={
                                                                onSetCurrentTab
                                                            }
                                                            overridingLocationSettings={
                                                                locationSettings
                                                            }
                                                        />
                                                    </Box>
                                                </DeviceOverview.DeviceSectionGroup>
                                            )}
                                            {isJerryBaitRat && (
                                                <DeviceOverview.DeviceBaitActivitiesChart
                                                    device={device}
                                                />
                                            )}
                                            <DeviceOverview.DeviceAdvancedInfo
                                                device={device}
                                                permissions={permissions}
                                                hasOccupancyRate={
                                                    hasOccupancyRate
                                                }
                                                isInsectTypeTom={
                                                    isInsectTypeTom
                                                }
                                                isDeviceTriggered={
                                                    isDeviceTriggered
                                                }
                                                lastTriggeredDateText={
                                                    lastTriggeredDateText
                                                }
                                                isTrapSensor={isTrapSensor}
                                                isJerryBaitRat={isJerryBaitRat}
                                            />
                                        </DeviceOverview>

                                        {/* <div className={classes.qrCodeWrapper}>
                                                <QRCode value={device.serial_number}/>
                                            </Box> */}
                                    </>
                                )}
                            </>
                        )}
                        {tab === 'reports' && <DeviceReports device={device} />}

                        {tab === 'pictures' && (
                            <>
                                <Box className={classes.picturesWrapper}>
                                    <DevicePictures deviceId={deviceId} />
                                </Box>
                            </>
                        )}

                        {tab === 'history' && (
                            <DeviceMoveHistory device={device} />
                        )}

                        {tab === 'settings' && (
                            <DeviceSettings
                                device={device}
                                canUpdate={permissions.updateDevice}
                                reloadDevice={() =>
                                    dispatch(actions.deviceRequest(deviceId))
                                }
                                overridingLocationSettings={locationSettings}
                            />
                        )}

                        <Modal
                            open={isResetModalOpen}
                            setOpen={setIsResetModalOpen}>
                            <Box className={classes.modalInnerWrapper}>
                                {locale.devices.resetDescr}
                                {/* ??? {!isTom && (
                                    <Alert className={classes.section} severity="warning">
                                        {locale.devices.resetDescrNote}
                                    </Alert>
                                )} */}
                                <Box className={classes.modalButtons}>
                                    <Button
                                        className={cl([classes.actionButton])}
                                        onClick={() => onReset(device.id)}>
                                        {locale.devices.reset}
                                    </Button>
                                    <Button
                                        reverted
                                        onClick={() =>
                                            setIsResetModalOpen(false)
                                        }>
                                        {locale.common.cancel}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        <Modal
                            open={isReportCatchModalOpen}
                            setOpen={setIsReportCatchModalOpen}>
                            <ReportCatchModal
                                deviceId={device.id}
                                onClose={closeReportCatchModal}
                                contextPage="device"
                            />
                        </Modal>
                        <Modal
                            open={isSetOfflineModalOpen}
                            setOpen={setIsSetOfflineModalOpen}>
                            <Box className={classes.modalInnerWrapper}>
                                {locale.devices.setOfflineDescr}
                                <Box className={classes.modalButtons}>
                                    <Button
                                        className={cl([classes.actionButton])}
                                        onClick={() => onSetOffline(device.id)}>
                                        {locale.devices.setOffline}
                                    </Button>
                                    <Button
                                        reverted
                                        onClick={() =>
                                            setIsSetOfflineModalOpen(false)
                                        }>
                                        {locale.common.cancel}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        <Modal
                            open={isBatteryModalOpen}
                            setOpen={setIsBatteryModalOpen}>
                            <Box className={classes.modalInnerWrapper}>
                                {locale.devices.resetBatteryDescr}
                                <Box className={classes.modalButtons}>
                                    <Button
                                        className={cl([classes.actionButton])}
                                        onClick={() =>
                                            onBatteryReset(device.id)
                                        }>
                                        {locale.devices.resetBattery}
                                    </Button>
                                    <Button
                                        reverted
                                        onClick={() =>
                                            setIsBatteryModalOpen(false)
                                        }>
                                        {locale.common.cancel}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        <Modal
                            open={isSetActiveTOMModalOpen}
                            setOpen={setIsSetActiveTOMModalOpen}>
                            <Box className={classes.modalInnerWrapper}>
                                {locale.devices.resetTOMStatusDescr}
                                <Box className={classes.modalButtons}>
                                    <Button
                                        className={cl([
                                            classes.actionButton,
                                            classes.resetBatteryModalBtn
                                        ])}
                                        onClick={() =>
                                            onSetActiveTOM(device.id)
                                        }>
                                        <Box>
                                            {locale.devices.resetTomStatus}
                                        </Box>
                                    </Button>
                                    <Button
                                        reverted
                                        onClick={() =>
                                            setIsSetActiveTOMModalOpen(false)
                                        }>
                                        {locale.common.cancel}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                        <Modal
                            open={isMaintenanceModalOpen}
                            setOpen={setIsMaintenanceModalOpen}>
                            <Box className={classes.modalInnerWrapper}>
                                {device.in_maintenance_mode
                                    ? locale.common.switchOffMModeDescr
                                    : locale.common.switchOnMModeDescr}
                                {!device.in_maintenance_mode && (
                                    <MaintenanceDurationPicker
                                        value={maintenanceMinutes}
                                        onChange={setMaintenanceMinutes}
                                        label={locale.common.maintenanceMode}
                                        onValidityChange={setIsDurationValid}
                                    />
                                )}
                                <Box className={classes.modalButtons}>
                                    <Button
                                        className={cl([classes.actionButton])}
                                        disabled={
                                            !device.in_maintenance_mode &&
                                            !isDurationValid
                                        }
                                        onClick={() =>
                                            onMaintenanceModeSet(
                                                device.id,
                                                device.in_maintenance_mode
                                            )
                                        }>
                                        {device.in_maintenance_mode
                                            ? locale.common.switchOffMMode
                                            : locale.common.switchOnMMode}
                                    </Button>
                                    <Button
                                        reverted
                                        onClick={() =>
                                            setIsMaintenanceModalOpen(false)
                                        }>
                                        {locale.common.cancel}
                                    </Button>
                                </Box>
                            </Box>
                        </Modal>
                    </React.Fragment>
                )}
                {!isLoading && !device && (
                    <Box>{locale.devices.deviceIsNotFound}</Box>
                )}
                <Modal open={isConfirmModal} setOpen={setIsConfirmModal}>
                    <Box>{locale.devices.changeOperationMode}</Box>
                    <Box className={classes.modalBody}>
                        <img
                            className={classes.imgInstruction}
                            // eslint-disable-next-line
                            src={require('../../assets/img/restart-device-insturction.png')}></img>
                    </Box>
                    <Box className={classes.modalButtons}>
                        <Button
                            reverted
                            onClick={() => setIsConfirmModal(false)}>
                            {locale.common.ok}
                        </Button>
                    </Box>
                </Modal>
            </Box>
        </Box>
    )
}

export default withRouter(Device)
